import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";

import { paper, fonts, easing, paperA, shade, stickyA } from "../theme";
import { getHostName, getPlayerName } from "../players";
import { useWebSocket } from "../useWebSocket";
import type { PlayerInfo, RoundLeaderboardView, CommonRoomData } from "../protocol";

import {
    MsScreen, MsBody, MsFooter,
    MsDisplay, MsEyebrow, MsPlate, MsReceipt,
    MsBtn, MsWaitingFor,
    MaskHeadline, Dots,
    Stack, Row,
} from "../ms";

const FormRow = styled.div<{ you?: boolean }>`
    display: grid;
    grid-template-columns: 28px 1fr auto;
    gap: 12px;
    align-items: center;
    padding: 14px 4px;
    border-bottom: 1px dashed ${shade(0.18)};
    position: relative;

    &:last-child { border-bottom: 0; }

    ${p => p.you && `
        &::before {
            content: '';
            position: absolute;
            left: -10px; top: 4px; bottom: 4px;
            width: 3px;
            background: ${paper.red};
            border-radius: 2px;
        }
    `}
`;

const Rank = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 800;
    font-size: 26px;
    line-height: 0.85;
    color: ${paper.ink};
    letter-spacing: -0.04em;
    font-variant-numeric: tabular-nums;
`;

const Pts = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 800;
    font-size: 26px;
    color: ${paper.ink};
    font-variant-numeric: tabular-nums;
`;

const Bar = styled.div<{ pct: number; delay: number; you?: boolean }>`
    grid-column: 1 / -1;
    position: relative;
    height: 3px;
    margin-top: 4px;
    background: ${shade(0.08)};

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        width: ${p => p.pct}%;
        background: ${p => p.you ? paper.red : paper.ink};
        animation: ms-bar-grow 1s ${p => p.delay}s ${easing.standard} both;
        border-radius: 1px;
    }
`;

const Heading = styled(MsDisplay)`
    font-size: 36px;
`;

const LeaderWrap = styled(motion.div)`
    margin-top: 4px;
`;

/* Wears tone="sticky"; only the wider tracking is local. */
const LeaderEyebrow = styled(MsEyebrow)`
    letter-spacing: 0.06em;
`;

/* Yellow "+N this round" pill on the leader plate. */
const GainBadge = styled.div`
    font-family: ${fonts.sans};
    font-weight: 800;
    font-size: 11px;
    color: ${paper.red};
    background: ${stickyA(0.95)};
    padding: 4px 10px;
    border-radius: 999px;
    letter-spacing: 0.04em;
`;

const LeaderMainRow = styled(Row)`
    margin-top: 10px;
`;

/* Oversized red "01" rank on the leader plate. */
const LeaderRankNum = styled.div`
    font-family: ${fonts.serif};
    font-weight: 800;
    font-size: 84px;
    line-height: 0.85;
    color: ${paper.red};
    letter-spacing: -0.04em;
`;

const LeaderInfo = styled.div`
    flex: 1;
`;

const LeaderName = styled.div`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 30px;
    color: ${paper.paper};
`;

const LeaderTotalRow = styled(Row)`
    border-top: 1px solid ${paperA(0.25)};
    margin-top: 8px;
    padding-top: 6px;
`;

const TotalEyebrow = styled(MsEyebrow)`
    color: ${paperA(0.55)};
`;

const LeaderScore = styled.span`
    font-family: ${fonts.serif};
    font-weight: 800;
    font-size: 32px;
    color: ${paper.paper};
`;

const LeaderScoreUnit = styled.span`
    font-size: 14px;
    color: ${paperA(0.55)};
    font-weight: 600;
    letter-spacing: 0.04em;
`;

const RestName = styled.div`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 20px;
    line-height: 1.15;
`;

const RestGainedEyebrow = styled(MsEyebrow)`
    margin-top: 2px;
`;

const RestGain = styled.span`
    color: ${paper.red};
    font-weight: 700;
`;

interface Props {
    view: RoundLeaderboardView;
    players: PlayerInfo[];
    self: PlayerInfo;
    common?: CommonRoomData;
}

export default function RoundLeaderboard({ view, players, self }: Props) {
    const { sendMessage } = useWebSocket();
    const { scores, roundResults } = view;
    const [revealDone, setRevealDone] = useState(false);

    const ranked = Object.entries(scores)
        .map(([playerId, score]) => ({
            playerId,
            name: getPlayerName(playerId, players),
            score: score as number,
            gained: (roundResults[playerId] as number) || 0,
            you: playerId === self?.id,
        }))
        .sort((a, b) => b.score - a.score);

    const max = ranked[0]?.score || 0;
    const leader = ranked[0];
    const rest = ranked.slice(1);

    useEffect(() => {
        const total = rest.length * 150 + 800;
        const t = setTimeout(() => setRevealDone(true), total);
        return () => clearTimeout(t);
    }, [rest.length]);

    const next = () => sendMessage({ type: "advance_results" });

    return (
        <MsScreen>
            <MsBody>
                <Stack gap={16}>
                    <Heading>
                        <MaskHeadline>Current standings</MaskHeadline>
                    </Heading>

                    {/* Leader hero plate */}
                    {leader && (
                        <LeaderWrap layoutId="winning-quote">
                        <MsPlate drop r1="-0.6deg">
                            <Row justify="space-between" align="center">
                                <LeaderEyebrow tone="sticky">Leader</LeaderEyebrow>
                                {leader.gained > 0 && (
                                    <GainBadge>+{leader.gained} this round</GainBadge>
                                )}
                            </Row>
                            <LeaderMainRow gap={16} align="flex-end">
                                <LeaderRankNum>01</LeaderRankNum>
                                <LeaderInfo>
                                    <LeaderName>
                                        {leader.name}
                                    </LeaderName>
                                    <LeaderTotalRow justify="space-between" align="baseline">
                                        <TotalEyebrow>total</TotalEyebrow>
                                        <LeaderScore>
                                            {leader.score}{" "}
                                            <LeaderScoreUnit>PTS</LeaderScoreUnit>
                                        </LeaderScore>
                                    </LeaderTotalRow>
                                </LeaderInfo>
                            </LeaderMainRow>
                        </MsPlate>
                        </LeaderWrap>
                    )}

                    {/* Rest of the field */}
                    {rest.length > 0 && (
                        <MsReceipt>
                            {rest.map((p, i) => {
                                const rank = i + 2;
                                const pct = Math.max(8, max > 0 ? (p.score / max) * 100 : 8);
                                return (
                                    <FormRow key={p.playerId} you={p.you}>
                                        <Rank>{String(rank).padStart(2, "0")}</Rank>
                                        <div>
                                            <RestName>{p.name}</RestName>
                                            <RestGainedEyebrow tone="mute">
                                                {p.gained > 0
                                                    ? <RestGain>+{p.gained}</RestGain>
                                                    : <span>—</span>}
                                            </RestGainedEyebrow>
                                        </div>
                                        <Pts>{p.score}</Pts>
                                        <Bar pct={pct} delay={0.5 + i * 0.1} you={p.you} />
                                    </FormRow>
                                );
                            })}
                        </MsReceipt>
                    )}
                </Stack>
            </MsBody>
            <MsFooter>
                {self?.isHost ? (
                    <MsBtn variant="red" onClick={next} disabled={!revealDone}>
                        {revealDone ? "Next round" : <>Tallying <Dots /></>}
                    </MsBtn>
                ) : (
                    <MsWaitingFor name={getHostName(players)} />
                )}
            </MsFooter>
        </MsScreen>
    );
}
